import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
  viewChild,
} from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { ButtonComponent } from '@libs/common/components';
import { IconComponent } from '@libs/components/icons';
import { FileInputAllowedTypeModel, FileInputModel, getAllowedFileTypes } from '@libs/form/models';
import { PhotoPickerService } from '@libs/shared/services';

// eslint-disable-next-line @typescript-eslint/typedef
export const DialogButtonStrings = {
  selectSingleFile: 'Datei auswählen',
  selectMultipleFiles: 'Dateien auswählen',
  replaceSingleFile: 'Datei erneut auswählen',
  addMultipleFiles: 'Weitere Dateien auswählen',
} as const;

// eslint-disable-next-line @typescript-eslint/typedef
export const CameraButtonStrings = {
  openCamera: 'Kamera öffnen',
  openCameraForSingleFile: 'Kamera erneut öffnen',
  openCameraForMultipleFiles: 'Kamera für weiteres Bild öffnen',
} as const;

// eslint-disable-next-line @typescript-eslint/typedef
export const PhotoButtonStrings = {
  addPhoto: 'Foto hinzufügen',
  addPhotoForSingleFile: 'Foto erneut hinzufügen',
  addPhotoForMultipleFiles: 'Weiteres Foto hinzufügen',
} as const;

// eslint-disable-next-line @typescript-eslint/typedef
export const PhotoMenuStrings = {
  takePhoto: 'Foto aufnehmen',
  mediaLibrary: 'Foto auswählen',
} as const;

const isImageType = (type: FileInputAllowedTypeModel): boolean => type.mimeType.startsWith('image/');

@Component({
  selector: 'ui-file-picker-actions',
  standalone: true,
  imports: [ButtonComponent, IconComponent, MatMenuModule],
  templateUrl: './file-picker-actions.component.html',
  styleUrls: ['./file-picker-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilePickerActionsComponent {
  private readonly photoPicker: PhotoPickerService = inject(PhotoPickerService);

  public readonly data: InputSignal<FileInputModel> = input.required();
  public readonly disabled: InputSignal<boolean> = input.required();
  public readonly currentFileCount: InputSignal<number> = input.required();

  public readonly inputElementRef: Signal<ElementRef<HTMLInputElement>> = viewChild.required('input');
  public readonly filesSelected: OutputEmitterRef<File[]> = output<File[]>();

  public readonly photoMenuStrings: typeof PhotoMenuStrings = PhotoMenuStrings;

  public readonly isMultiple: Signal<boolean> = computed(() => Boolean(this.data().attributes?.multiple));
  public readonly enableCamera: Signal<boolean> = computed(() => Boolean(this.data().attributes?.camera));
  public readonly enableFileDialog: Signal<boolean> = computed(() => this.isMultiple() || !this.enableCamera());

  // The native app offers camera and media library when the input allows images
  public readonly enablePhotoPicker: Signal<boolean> = computed(() => {
    return this.photoPicker.isAvailable() && getAllowedFileTypes(this.data()).some(isImageType);
  });

  // Photos have their own button in the native app. Without image types the file dialog opens the files directly.
  public readonly acceptFiles: Signal<string> = computed(() => {
    const types: FileInputAllowedTypeModel[] = getAllowedFileTypes(this.data())
      .filter(type => !this.enablePhotoPicker() || !isImageType(type));

    return [
      ...types.map(type => type.mimeType),
      ...types.flatMap(type => type.extensions).map(ext => `.${ext}`),
    ].join(',');
  });

  // Number of files that still fit. Undefined means that there is no maximum.
  public readonly remainingFileCount: Signal<number | undefined> = computed(() => {
    const maxCount: number | undefined = this.data().attributes?.maxCount;

    return maxCount !== undefined
      ? maxCount - this.currentFileCount()
      : undefined;
  });

  public readonly fileButtonText: Signal<string> = computed(() => {
    if (this.currentFileCount() === 0) {
      return this.isMultiple()
        ? DialogButtonStrings.selectMultipleFiles
        : DialogButtonStrings.selectSingleFile;
    }

    return this.isMultiple()
      ? DialogButtonStrings.addMultipleFiles
      : DialogButtonStrings.replaceSingleFile;
  });

  public readonly cameraButtonText: Signal<string> = computed(() => {
    if (this.currentFileCount() === 0) {
      return CameraButtonStrings.openCamera;
    }

    return this.isMultiple()
      ? CameraButtonStrings.openCameraForMultipleFiles
      : CameraButtonStrings.openCameraForSingleFile;
  });

  public readonly photoButtonText: Signal<string> = computed(() => {
    if (this.currentFileCount() === 0) {
      return PhotoButtonStrings.addPhoto;
    }

    return this.isMultiple()
      ? PhotoButtonStrings.addPhotoForMultipleFiles
      : PhotoButtonStrings.addPhotoForSingleFile;
  });

  public pickFiles(mode: 'camera' | 'dialog'): void {
    const fileInput: HTMLInputElement = this.inputElementRef().nativeElement;

    if (mode === 'camera') {
      fileInput.accept = 'image/*';
      fileInput.setAttribute('capture', 'environment');
    } else {
      fileInput.accept = this.acceptFiles();
      // iOS opens the camera as long as the capture attribute exists, also when it is empty
      fileInput.removeAttribute('capture');
    }

    fileInput.click();
  }

  public async takePhoto(): Promise<void> {
    if (this.canAddPhotos()) {
      this.emitPhotos(await this.photoPicker.takePhoto());
    }
  }

  public async pickFromMediaLibrary(): Promise<void> {
    if (this.canAddPhotos()) {
      this.emitPhotos(await this.photoPicker.pickFromMediaLibrary(this.isMultiple(), this.remainingFileCount()));
    }
  }

  public onInputChange(): void {
    const fileInput: HTMLInputElement = this.inputElementRef().nativeElement;

    /* istanbul ignore else */
    if (fileInput.files?.length) {
      this.filesSelected.emit(Array.from(fileInput.files));
    }

    fileInput.value = '';
  }

  // The plugin reads a limit below 1 as no limit so the picker stays closed
  private canAddPhotos(): boolean {
    const remainingFileCount: number | undefined = this.remainingFileCount();

    return !this.disabled() && (remainingFileCount === undefined || remainingFileCount > 0);
  }

  private emitPhotos(photos: File[]): void {
    if (photos.length) {
      this.filesSelected.emit(photos);
    }
  }
}
